import { SelectMenu } from '@scale/scaleui';
import { memo, useState } from 'react';
import {
  DEFAULT_NER_TITLE,
  DEFAULT_NER_SUBTITLE,
  InteractionStepType,
} from '@scale/shared/types/chat';
import StringInputBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/StringInputBuilder';
import {
  ReviewLevelConfig,
  ConditionalStepConfig,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/definitions/configs';
import LabelsBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/LabelsBuilder';
import { INamedEntityRecognitionLabel } from 'react-scale/components/scalers/tasks/namedEntityRecognition/namedEntityRecognitionLabels';
import BooleanInputBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/BooleanInputBuilder';
import produce from 'immer';
import { AttributePerNERConfig } from '../configs/AttributePerNERConfig';
import { StepConfigurationComponentProps } from '../stepDescriptor/types';
import { typeBuilderMap } from 'react-scale/corp/components/QM/taskTemplatesEditor/BuilderSchemas';

const NER_SOURCES = [
  InteractionStepType.TextCollection,
  InteractionStepType.ModelResponseSelector,
  InteractionStepType.PromptInput,
];
const SUPPORTED_FIELD_TYPES = ['text'];

function NamedEntityRecognitionConfigurationComponent({
  params: currentParams,
  setParams: setCurrentParams,
  previousParams,
  taskParams,
}: StepConfigurationComponentProps<InteractionStepType.NER>) {
  const [selectedTextField, setSelectedTextField] = useState('');
  const nerBaseParams = previousParams.filter(params => NER_SOURCES.includes(params.type));
  const [selectedStep, setSelectedStep] = useState(
    () => currentParams.params.provided_text_step_id || '',
  );

  const previousSteps = nerBaseParams.map(params => ({
    id: params.id,
    name: `(#${params.id}) ${params.type}`,
  }));

  let textFieldError = false;
  let textFieldOptions: { id: string; name: string }[] = [];

  const selectedStepParams = nerBaseParams.find(params => params.id === selectedStep);
  if (selectedStepParams?.type === InteractionStepType.TextCollection) {
    const fields = selectedStepParams.params.fields.filter((field: any) =>
      SUPPORTED_FIELD_TYPES.includes(field.type),
    );
    const fieldOptions = fields.map((field: any) => ({
      id: field.field_id,
      name: `${field.field_id}(${field.type}) - ${field.title}`,
    }));

    textFieldOptions = fieldOptions;
    textFieldError = fieldOptions.length === 0;
  }

  return (
    <div className="flex flex-col gap-4">
      <StringInputBuilder
        name="Title"
        placeholder={DEFAULT_NER_TITLE}
        existingValue={currentParams.title}
        updateProperty={(value: string) =>
          setCurrentParams(
            produce(currentParams, draft => {
              draft.title = value;
            }),
          )
        }
        required
      />
      <StringInputBuilder
        name="Subtitle"
        placeholder={DEFAULT_NER_SUBTITLE}
        existingValue={currentParams.subtitle}
        updateProperty={(value: string) =>
          setCurrentParams(
            produce(currentParams, draft => {
              draft.subtitle = value;
            }),
          )
        }
      />
      <SelectMenu
        label="Choose a step to populate the content for labeling"
        placeholder="Select a step"
        options={previousSteps}
        selectedOptionId={selectedStep}
        setSelected={(value: string) => {
          setSelectedStep(value);
          setCurrentParams(
            produce(currentParams, draft => {
              draft.params.provided_text_step_id = value;
              draft.params.provided_model_id = undefined;
            }),
          );
        }}
        required
      />
      {textFieldError ? (
        <div className="text-danger-500">
          Selected step does not have any text fields. Please select another step.
        </div>
      ) : null}
      {textFieldOptions.length > 0 ? (
        <SelectMenu
          label="Text Collection Fields"
          placeholder="Select a field"
          options={textFieldOptions}
          selectedOptionId={selectedTextField}
          setSelected={(value: string) => {
            setSelectedTextField(value);
            setCurrentParams(
              produce(currentParams, draft => {
                draft.params.provided_text_field_id = value;
              }),
            );
          }}
        />
      ) : null}
      {selectedStepParams?.type === InteractionStepType.ModelResponseSelector ? (
        <>
          <StringInputBuilder
            name="Model Response ID"
            existingValue={currentParams.params.provided_model_id}
            updateProperty={(value: string) =>
              setCurrentParams(
                produce(currentParams, draft => {
                  draft.params.provided_model_id = value;
                }),
              )
            }
          />
          <div className="prose text-sm italic">
            Specify a model response ID from the Response Selector step to populate the labeled
            content. If empty, the labeled content will be populated from the selected model from
            the response selector step.
          </div>
        </>
      ) : null}
      <LabelsBuilder
        typeBuilderMap={typeBuilderMap}
        isTopLevel
        name="Labels"
        description="Add labels for NER annotation."
        existingValue={currentParams.params.labels}
        updateProperty={(value: INamedEntityRecognitionLabel[]) =>
          setCurrentParams(
            produce(currentParams, draft => {
              draft.params.labels = value;
            }),
          )
        }
      />
      <AttributePerNERConfig currentParams={currentParams} setCurrentParams={setCurrentParams} />
      <div className="flex flex-col gap-2">
        <BooleanInputBuilder
          name="Allow Overlapping Annotations"
          existingValue={!!currentParams.params.allow_overlapping_annotations}
          updateProperty={(value: boolean) =>
            setCurrentParams(
              produce(currentParams, draft => {
                draft.params.allow_overlapping_annotations = value;
              }),
            )
          }
        />
      </div>
      <ReviewLevelConfig currentParams={currentParams} setCurrentParams={setCurrentParams} />

      <ConditionalStepConfig
        currentParams={currentParams}
        setCurrentParams={setCurrentParams}
        previousParams={previousParams}
        taskParams={taskParams}
      />
    </div>
  );
}

export default memo(NamedEntityRecognitionConfigurationComponent);
